import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

function isTimezoneOffset(timeZone: string): boolean {
  return /^[-+]?\d{1,2}(?::?\d{2})?$/.test(timeZone.trim());
}

export function getTimezoneAbbreviation(
  date: Date,
  timeZone: string,
): string | undefined {
  if (isTimezoneOffset(timeZone)) {
    return undefined;
  }

  try {
    return (
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName: "short",
      })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value
    );
  } catch {
    return undefined;
  }
}

export interface DateTimeFieldStoredValue {
  isoString: string;
  timezoneOffset: string | undefined;
  ianaTimezone?: string | undefined;
}

export function formatStoredDateTimeFieldValue(
  value: DateTimeFieldStoredValue,
  format: string,
): string {
  const date = dayjs(value.isoString);
  const formattedValue = value.ianaTimezone
    ? date.tz(value.ianaTimezone).format(format)
    : date.format(format);
  const timezoneAbbreviation = value.ianaTimezone
    ? getTimezoneAbbreviation(new Date(value.isoString), value.ianaTimezone)
    : undefined;

  return timezoneAbbreviation
    ? `${formattedValue} ${timezoneAbbreviation}`
    : formattedValue;
}

export type DateTimeFieldValue =
  | string
  | number
  | DateTimeFieldStoredValue;

export function isDateTimeFieldStoredValue(
  value: unknown,
): value is DateTimeFieldStoredValue {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  if (!("isoString" in value) || typeof value.isoString !== "string") {
    return false;
  }

  if (
    "ianaTimezone" in value &&
    value.ianaTimezone !== undefined &&
    typeof value.ianaTimezone !== "string"
  ) {
    return false;
  }

  if (
    "timezoneOffset" in value &&
    value.timezoneOffset !== undefined &&
    typeof value.timezoneOffset !== "string"
  ) {
    return false;
  }

  if (
    "timezone" in value &&
    value.timezone !== undefined &&
    typeof value.timezone !== "string"
  ) {
    return false;
  }

  return true;
}

export function getDateTimeFieldTimezoneOffset(
  value: DateTimeFieldStoredValue,
): string | undefined {
  if (value.timezoneOffset !== undefined) {
    return value.timezoneOffset;
  }

  if ("timezone" in value && typeof value.timezone === "string") {
    return value.timezone;
  }

  return undefined;
}

export function getDateTimeFieldValue(
  value: unknown,
): DateTimeFieldValue | undefined {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    isDateTimeFieldStoredValue(value)
  ) {
    return value;
  }

  return undefined;
}

export function getDateTimeFieldIsoString(
  value: DateTimeFieldValue,
): string | number {
  if (isDateTimeFieldStoredValue(value)) {
    return value.isoString;
  }

  return value;
}

export function getDateTimeFieldTimestamp(value: unknown): number | undefined {
  const fieldValue = getDateTimeFieldValue(value);

  if (fieldValue === undefined) {
    return undefined;
  }

  if (typeof fieldValue === "number") {
    return Number.isFinite(fieldValue) ? fieldValue : undefined;
  }

  const timestamp = new Date(getDateTimeFieldIsoString(fieldValue)).getTime();
  return Number.isFinite(timestamp) ? timestamp : undefined;
}
